import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { Building2, Calendar, Inbox, Plus, Edit, Trash2, FileText, Newspaper, Image as ImageIcon, ExternalLink, Download, Eye, Users, KeyRound, Receipt, AlertTriangle, MessageSquare, ClipboardList, TrendingUp, ListChecks, ArrowUp, ArrowDown } from "lucide-react";
import { toast, Toaster } from "sonner";

const STATUS_LABEL = { disponible: "Disponible", occupe: "Occupé", loue: "Loué" };
const STATUS_CLASS = {
  disponible: "bg-[#FFC107]/20 border-slate-900 text-slate-900",
  occupe: "bg-orange-500/20 border-orange-700 text-orange-900",
  loue: "bg-slate-200 border-slate-700 text-slate-700",
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("properties");
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [posts, setPosts] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    const [s, p, r, c, b] = await Promise.all([
      api.get("/admin/stats").then(r => r.data),
      api.get("/properties").then(r => r.data),
      api.get("/admin/reservations").then(r => r.data),
      api.get("/admin/contact-requests").then(r => r.data),
      api.get("/admin/blog").then(r => r.data),
    ]);
    setStats(s); setProperties(p); setReservations(r); setContacts(c); setPosts(b);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const removeProperty = async (id) => {
    if (!confirm("Supprimer ce local ?")) return;
    await api.delete(`/admin/properties/${id}`);
    toast.success("Local supprimé");
    load();
  };

  const updatePropertyStatus = async (p, status) => {
    await api.put(`/admin/properties/${p.id}`, { status });
    toast.success("Statut mis à jour");
    load();
  };

  const updatePropertyOrder = async (id, order) => {
    const n = parseInt(order, 10);
    if (Number.isNaN(n)) return;
    await api.put(`/admin/properties/${id}`, { order: n });
    load();
  };

  const swapPropertyOrder = async (idx, direction) => {
    const j = direction === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= properties.length) return;
    const a = properties[idx], b = properties[j];
    const oa = Number.isFinite(a.order) ? a.order : 100;
    const ob = Number.isFinite(b.order) ? b.order : 100;
    // If both have same order, give the moved one a slightly different value
    const [newA, newB] = oa === ob ? (direction === "up" ? [ob - 1, ob] : [ob + 1, ob]) : [ob, oa];
    await Promise.all([
      api.put(`/admin/properties/${a.id}`, { order: newA }),
      api.put(`/admin/properties/${b.id}`, { order: newB }),
    ]);
    load();
  };

  const updateReservationStatus = async (id, status) => {
    await api.put(`/admin/reservations/${id}`, { status });
    toast.success("Statut mis à jour");
    load();
  };

  const deletePost = async (id) => {
    if (!confirm("Supprimer cet article ?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Article supprimé");
    load();
  };

  const updatePostOrder = async (id, order) => {
    const n = parseInt(order, 10);
    if (Number.isNaN(n)) return;
    await api.put(`/admin/blog/${id}`, { order: n });
    load();
  };

  const swapPostOrder = async (idx, direction) => {
    const j = direction === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= posts.length) return;
    const a = posts[idx], b = posts[j];
    const oa = Number.isFinite(a.order) ? a.order : 100;
    const ob = Number.isFinite(b.order) ? b.order : 100;
    const [newA, newB] = oa === ob ? (direction === "up" ? [ob - 1, ob] : [ob + 1, ob]) : [ob, oa];
    await Promise.all([
      api.put(`/admin/blog/${a.id}`, { order: newA }),
      api.put(`/admin/blog/${b.id}`, { order: newB }),
    ]);
    load();
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-3">— Back-office</div>
        <div className="flex items-start justify-between mb-10 gap-4 flex-wrap">
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">Tableau de bord</h1>
          <Link to="/backoffice-finance" data-testid="link-backoffice-finance"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-amber-300 border border-slate-900 hover:bg-slate-800 font-bold text-sm uppercase tracking-wider">
            <TrendingUp className="w-4 h-4" /> Pilotage Foncière (Groupe)
          </Link>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            <Stat label="Locaux" value={stats.total_properties} sub={`${stats.available_properties} dispo`} />
            <Stat label="Réservations" value={stats.total_reservations} sub={`${stats.pending_reservations} en attente`} />
            <Stat label="Demandes" value={stats.total_contacts} sub="contacts" />
            <Stat label="Articles" value={stats.total_posts || 0} sub="blog" />
            <Stat label="Site" value="ON" sub="en ligne" highlight />
          </div>
        )}

        <div className="flex gap-2 border-b border-slate-900/15 mb-8 overflow-x-auto">
          <TabBtn active={tab === "properties"} onClick={() => setTab("properties")} icon={Building2} label="Locaux" testId="tab-properties" />
          <TabBtn active={tab === "reservations"} onClick={() => setTab("reservations")} icon={Calendar} label="Réservations" testId="tab-reservations" />
          <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")} icon={Inbox} label="Demandes" testId="tab-contacts" />
          <TabBtn active={tab === "blog"} onClick={() => setTab("blog")} icon={Newspaper} label="Blog" testId="tab-blog" />
          <Link to="/admin/pages" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-pages"><FileText className="w-4 h-4"/>Pages <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/contenu" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-content"><ImageIcon className="w-4 h-4"/>Contenu <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/textes" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-texts"><FileText className="w-4 h-4"/>Textes <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/activites" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-activities"><Building2 className="w-4 h-4"/>Activités <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/locataires" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-tenants"><Users className="w-4 h-4"/>Locataires <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/baux" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-leases"><KeyRound className="w-4 h-4"/>Baux <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/loyers" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-rents"><Receipt className="w-4 h-4"/>Loyers <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/incidents" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-incidents"><AlertTriangle className="w-4 h-4"/>Incidents <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/messages" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-messages"><MessageSquare className="w-4 h-4"/>Messages <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/etats-des-lieux" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-inventories"><ClipboardList className="w-4 h-4"/>États des lieux <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/irl" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-irl"><TrendingUp className="w-4 h-4"/>IRL <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/medias" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-media"><ImageIcon className="w-4 h-4"/>Médias <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/temoignages" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-testimonials"><Newspaper className="w-4 h-4"/>Témoignages <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/partenaires" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-partners"><Building2 className="w-4 h-4"/>Partenaires <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/utilisateurs" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-users"><Building2 className="w-4 h-4"/>Utilisateurs <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/formulaires" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-forms"><ListChecks className="w-4 h-4"/>Formulaires <ExternalLink className="w-3 h-3"/></Link>
          <Link to="/admin/tracking" className="px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap border-transparent text-slate-500 hover:text-slate-900" data-testid="tab-tracking"><TrendingUp className="w-4 h-4"/>Tracking <ExternalLink className="w-3 h-3"/></Link>
        </div>

        {tab === "properties" && (
          <div>
            <div className="flex justify-end mb-4">
              <Link to="/admin/biens/nouveau" data-testid="add-property-btn"
                className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> Ajouter un local
              </Link>
            </div>
            <div className="bg-white border border-slate-900/15 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <Th>Ordre</Th><Th>Titre</Th><Th>Surface</Th><Th>Loyer</Th><Th>Statut</Th><Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((p, idx) => (
                    <tr key={p.id} className="border-t border-slate-900/10" data-testid={`row-property-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <input type="number" value={p.order ?? 100}
                            onChange={e => setProperties(prev => prev.map(x => x.id === p.id ? { ...x, order: e.target.value === "" ? "" : parseInt(e.target.value, 10) } : x))}
                            onBlur={e => updatePropertyOrder(p.id, e.target.value)}
                            data-testid={`order-input-property-${p.id}`}
                            className="w-16 px-2 py-1 border border-slate-900/30 text-xs font-mono text-center" />
                          <button onClick={() => swapPropertyOrder(idx, "up")} disabled={idx === 0}
                            data-testid={`order-up-property-${p.id}`}
                            title="Monter" className="p-1 border border-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-current">
                            <ArrowUp className="w-3 h-3"/>
                          </button>
                          <button onClick={() => swapPropertyOrder(idx, "down")} disabled={idx === properties.length - 1}
                            data-testid={`order-down-property-${p.id}`}
                            title="Descendre" className="p-1 border border-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-current">
                            <ArrowDown className="w-3 h-3"/>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold">{p.title}</td>
                      <td className="px-4 py-3 font-mono">{p.surface_min}–{p.surface_max} m²</td>
                      <td className="px-4 py-3 font-mono">{p.price_monthly.toLocaleString("fr-FR")} €</td>
                      <td className="px-4 py-3">
                        <select value={p.status || "disponible"} onChange={e => updatePropertyStatus(p, e.target.value)}
                          data-testid={`status-select-${p.id}`}
                          className={`text-xs font-bold uppercase tracking-wider px-2 py-1 border ${STATUS_CLASS[p.status || "disponible"]} focus:outline-none`}>
                          {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right flex justify-end gap-2">
                        <a href={`/locaux/${p.slug || p.id}`} target="_blank" rel="noreferrer" title="Voir" className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white" data-testid={`view-${p.id}`}><ExternalLink className="w-4 h-4"/></a>
                        <button onClick={() => navigate(`/admin/biens/${p.id}/edit`)} data-testid={`edit-${p.id}`} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white"><Edit className="w-4 h-4"/></button>
                        <button onClick={() => removeProperty(p.id)} data-testid={`delete-${p.id}`} className="p-2 border border-slate-900 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 className="w-4 h-4"/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "reservations" && (
          <div>
            <div className="flex justify-end mb-4">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/reservations/export.csv`} target="_blank" rel="noreferrer"
                data-testid="export-csv-btn"
                className="btn-brutal bg-white border border-slate-900 px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2">
                <Download className="w-4 h-4" /> Exporter CSV
              </a>
            </div>
            <div className="bg-white border border-slate-900/15 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-white">
                  <tr><Th>Date</Th><Th>Type</Th><Th>Client</Th><Th>Entreprise</Th><Th>Local</Th><Th>Dossier</Th><Th>Paiement</Th><Th>Statut</Th><Th className="text-right">Action</Th></tr>
                </thead>
                <tbody>
                  {reservations.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-slate-500 font-mono">Aucune réservation pour l'instant.</td></tr>}
                  {reservations.map(r => (
                    <tr key={r.id} className="border-t border-slate-900/10" data-testid={`row-reservation-${r.id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{new Date(r.created_at).toLocaleDateString("fr-FR")}</td>
                      <td className="px-4 py-3"><span className="tag">{r.type}</span></td>
                      <td className="px-4 py-3"><div className="font-semibold">{r.full_name}</div><div className="text-xs text-slate-500">{r.email}</div></td>
                      <td className="px-4 py-3 text-xs">{r.company_info?.legal_name || r.company || "—"}</td>
                      <td className="px-4 py-3 text-xs">{r.property_title || r.property_id}</td>
                      <td className="px-4 py-3 text-xs font-mono">{r.flow_mode || "—"}{r.bilan?.documents?.length ? ` · ${r.bilan.documents.length} doc` : ""}</td>
                      <td className="px-4 py-3 text-xs font-mono">{r.payment_status}</td>
                      <td className="px-4 py-3">
                        <select value={r.status} onChange={e => updateReservationStatus(r.id, e.target.value)}
                          data-testid={`status-${r.id}`}
                          className="border border-slate-900/30 px-2 py-1 text-xs font-mono">
                          <option value="nouveau">Nouveau</option>
                          <option value="contacte">Contacté</option>
                          <option value="confirme">Confirmé</option>
                          <option value="annule">Annulé</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => navigate(`/admin/reservations/${r.id}`)} data-testid={`view-reservation-${r.id}`}
                          title="Voir le dossier complet"
                          className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white">
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div className="bg-white border border-slate-900/15 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-white">
                <tr><Th>Date</Th><Th>Client</Th><Th>Surface</Th><Th>Activité</Th><Th>Message</Th></tr>
              </thead>
              <tbody>
                {contacts.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-slate-500 font-mono">Aucune demande pour l'instant.</td></tr>}
                {contacts.map(c => (
                  <tr key={c.id} className="border-t border-slate-900/10" data-testid={`row-contact-${c.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-4 py-3"><div className="font-semibold">{c.full_name}</div><div className="text-xs text-slate-500">{c.email} · {c.phone}</div></td>
                    <td className="px-4 py-3 text-xs">{c.surface_needed || "—"}</td>
                    <td className="px-4 py-3 text-xs">{c.activity || "—"}</td>
                    <td className="px-4 py-3 text-xs max-w-md">{c.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "blog" && (
          <div>
            <div className="flex justify-end mb-4">
              <Link to="/admin/blog/nouveau" data-testid="add-post-btn"
                className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> Nouvel article
              </Link>
            </div>
            <div className="bg-white border border-slate-900/15 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-white">
                  <tr><Th>Ordre</Th><Th>Titre</Th><Th>Catégorie</Th><Th>Slug</Th><Th>Publié</Th><Th className="text-right">Actions</Th></tr>
                </thead>
                <tbody>
                  {posts.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-500 font-mono">Aucun article.</td></tr>}
                  {posts.map((p, idx) => (
                    <tr key={p.id} className="border-t border-slate-900/10" data-testid={`row-post-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <input type="number" value={p.order ?? 100}
                            onChange={e => setPosts(prev => prev.map(x => x.id === p.id ? { ...x, order: e.target.value === "" ? "" : parseInt(e.target.value, 10) } : x))}
                            onBlur={e => updatePostOrder(p.id, e.target.value)}
                            data-testid={`order-input-post-${p.id}`}
                            className="w-16 px-2 py-1 border border-slate-900/30 text-xs font-mono text-center" />
                          <button onClick={() => swapPostOrder(idx, "up")} disabled={idx === 0}
                            data-testid={`order-up-post-${p.id}`}
                            title="Monter" className="p-1 border border-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-current">
                            <ArrowUp className="w-3 h-3"/>
                          </button>
                          <button onClick={() => swapPostOrder(idx, "down")} disabled={idx === posts.length - 1}
                            data-testid={`order-down-post-${p.id}`}
                            title="Descendre" className="p-1 border border-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-current">
                            <ArrowDown className="w-3 h-3"/>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold">{p.title}</td>
                      <td className="px-4 py-3 text-xs"><span className="tag">{p.category}</span></td>
                      <td className="px-4 py-3 font-mono text-xs">/blog/{p.slug}</td>
                      <td className="px-4 py-3 text-xs">{p.published ? "✓" : "—"}</td>
                      <td className="px-4 py-3 text-right flex justify-end gap-2">
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white" data-testid={`view-post-${p.id}`}><ExternalLink className="w-4 h-4"/></a>
                        <button onClick={() => navigate(`/admin/blog/${p.id}/edit`)} data-testid={`edit-post-${p.id}`} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white"><Edit className="w-4 h-4"/></button>
                        <button onClick={() => deletePost(p.id)} data-testid={`delete-post-${p.id}`} className="p-2 border border-slate-900 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 className="w-4 h-4"/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

const Stat = ({ label, value, sub, highlight }) => (
  <div className={`p-5 border ${highlight ? "bg-[#FFC107] border-slate-900" : "bg-white border-slate-900/15"}`}>
    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
    <div className="font-display font-black text-3xl mt-1">{value}</div>
    <div className="text-xs text-slate-500 mt-1 font-mono">{sub}</div>
  </div>
);
const TabBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap ${active ? "border-[#FFC107] text-slate-900" : "border-transparent text-slate-500"}`}>
    <Icon className="w-4 h-4" /> {label}
  </button>
);
const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-widest ${className}`}>{children}</th>
);
